import { BaseSyntheticEvent, useCallback, useState } from 'react';
// Redux
import { useSelector, useDispatch } from 'react-redux';
import {
  setShowCreateCampaignForm,
  setShowCancelCampaignForm,
} from '@Modal/reducers/campaigns/createCampaign/createCampaignSlice';

// Form
import { useFormState } from 'react-hook-form';

// System
import { useSnackbar } from 'notistack';

// Types
import { AppDispatch } from 'src/app/store';
import { StoreInterface } from 'src/app/types';
import {
  useCreateFreeBetCampaignMutation,
  PaginatedFreeBetsCampaignsDocument,
} from '@Src/generated.graphql';
import { useCreateCampaignForm } from '@Forms/campaign/CreateCampaignFormContext';
import { useCampaignsTable } from './useCampaignsTable';

export const useCreateCampaign = () => {
  // Redux
  const dispatch = useDispatch<AppDispatch>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [createCampaign] = useCreateFreeBetCampaignMutation();
  const { limit, page, campaignTableFilters } = useCampaignsTable();
  const { showCancelCampaignForm, showCreateCampaignForm } = useSelector(
    (state: StoreInterface) => state?.createCampaign
  );

  // Other Hooks
  const { enqueueSnackbar } = useSnackbar();

  // Handle Form Cancels
  const handleCreateCancel = () => {
    if (isDirty) {
      return dispatch(setShowCancelCampaignForm(true));
    } else {
      return dispatch(setShowCreateCampaignForm(false));
    }
  };

  const handleCancelTakeMeBack = (action: string) => {
    if (action === 'back') {
      dispatch(setShowCancelCampaignForm(false));
      dispatch(setShowCreateCampaignForm(true));
    } else {
      reset();
      dispatch(setShowCancelCampaignForm(false));
      dispatch(setShowCreateCampaignForm(false));
    }
  };

  // Form Controls
  const { control, handleSubmit, reset } = useCreateCampaignForm();
  const { isDirty, errors } = useFormState({ control });

  const handleSubmitCreateCampaign = useCallback(
    (e?: BaseSyntheticEvent) => {
      handleSubmit(async (formData) => {
        setIsSubmitting(true);

        try {
          await createCampaign({
            variables: {
              input: formData,
            },
            refetchQueries: [
              {
                query: PaginatedFreeBetsCampaignsDocument,
                variables: { limit, page, filter: campaignTableFilters, search: '' },
              },
            ],
          });

          enqueueSnackbar('Campaign was successfully created', {
            variant: 'success',
            autoHideDuration: 2000,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          });

          reset();
          dispatch(setShowCreateCampaignForm(false));
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Unknown error';
          enqueueSnackbar(message, {
            variant: 'error',
            autoHideDuration: 2000,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          });
        } finally {
          setIsSubmitting(false);
        }
      })(e);
    },
    // [createCampaign]
    []
  );

  const handleOpenCreateCampaignModal = () => dispatch(setShowCreateCampaignForm(true));

  return {
    // Store
    showCancelCampaignForm,
    showCreateCampaignForm,
    submittable: isDirty && Object.values(errors).length == 0 && !isSubmitting,
    // Actions
    handleCreateCancel,
    handleCancelTakeMeBack,
    setShowCreateCampaignForm,
    handleSubmitCreateCampaign,
    handleOpenCreateCampaignModal,
  };
};
